import { CSRF_HEADER_NAME } from '@/util/user'
import { giphyDarkestGrey } from '@giphy/colors'
import { setGADataLayer, setUser } from 'analytics'
import { useContext, useReducer, useRef, useState } from 'react'
import { ILoggedInUser } from 'types'
import { getErrorMessages } from 'utils/src/api'
import { TimeoutError, timeoutFetch } from 'utils/src/api/fetch'
import { FlashMessageContext } from '../../context/flash-message'
import { appleLogo, fbLogo } from '../../util/asset-url'
import { giphyLocalUrl, giphyUrl } from '@/app/util/env'
import UserContext from '@/context/user-context'
import { getLoginCSRFToken } from './login-csrf'
import PasswordVisibility from './password-visibility'
import { Button, ForgotPassword, Form, SocButton, SocialButtons, Terms } from './sc'
import { useSocialAuthPopup } from './use-social-auth-popup'

type LoginResponse = Pick<ILoggedInUser, 'id' | 'is_staff' | 'is_verified' | 'user_type'> & {
    redirect_url?: string
}

export type LoginFormCopy = {
    appleLogin: string
    emailPlaceholder: string
    errors: {
        refreshSession: string
        requestFailed: string
        timeout: string
    }
    facebookLogin: string
    forgotPassword: string
    passwordPlaceholder: string
    passwordVisibility: {
        hide: string
        show: string
    }
    socialAuth: {
        checkLogin: string
        finishedPrompt: string
        pending: string
    }
    submit: string
    terms: {
        conjunction: string
        prefix: string
        privacyPolicy: string
        termsOfService: string
    }
}

export const defaultLoginFormCopy: LoginFormCopy = {
    appleLogin: 'Log in with Apple',
    emailPlaceholder: 'Email Address',
    errors: {
        refreshSession: 'Login succeeded, but we could not refresh your session. Please try again.',
        requestFailed: 'We could not log you in. Please try again.',
        timeout: 'Login is taking longer than expected. Please try again.',
    },
    facebookLogin: 'Log in with Facebook',
    forgotPassword: 'Forgot Your Password?',
    passwordPlaceholder: 'Password',
    passwordVisibility: {
        hide: 'Hide password.',
        show: 'Show password as plain text. Warning: this will display your password on the screen.',
    },
    socialAuth: {
        checkLogin: 'Check login',
        finishedPrompt: 'Finished logging in?',
        pending: 'Complete login in the popup window.',
    },
    submit: 'Log In',
    terms: {
        conjunction: 'and',
        prefix: "By logging in you agree to GIPHY's",
        privacyPolicy: 'Privacy Policy',
        termsOfService: 'Terms of Service',
    },
}

type LoginFormProps = {
    copy?: LoginFormCopy
    mode?: 'modal' | 'page'
    next?: string
    onSuccess?: (user: ILoggedInUser) => void | Promise<void>
}

const LoginForm = ({ copy = defaultLoginFormCopy, mode = 'page', next, onSuccess }: LoginFormProps) => {
    const form = useRef<HTMLFormElement>(null)
    const loginInFlight = useRef(false)
    const [loading, setLoading] = useState(false)
    const emailInput = useRef<HTMLInputElement>(null)
    const passwordInput = useRef<HTMLInputElement>(null)
    const [viewPassword, setViewPassword] = useState(false)
    const [, forceUpdate] = useReducer((x) => x + 1, 0)
    const { showMessage } = useContext(FlashMessageContext)
    const { refreshUser } = useContext(UserContext)
    const facebookAuthHref = `${giphyUrl}/login/facebook/`
    const appleAuthHref = `${giphyUrl}/login/apple/`
    const { completeSocialAuth, openSocialAuthPopup, socialAuthState } = useSocialAuthPopup({ mode, onSuccess })
    const socialAuthPending = socialAuthState.status === 'pending'
    const login = async () => {
        if (loginInFlight.current) {
            return
        }

        loginInFlight.current = true
        setLoading(true)
        const loginData = new FormData(form.current!)
        if (next) {
            loginData.append('next', next)
        }
        try {
            const csrftoken = await getLoginCSRFToken()
            const result = await timeoutFetch({
                url: `${giphyLocalUrl}/api/v1/users/login/`,
                options: {
                    method: 'POST',
                    body: loginData,
                    headers: {
                        [CSRF_HEADER_NAME]: csrftoken,
                    },
                },
                timeout: 10000,
            })
            const responseBody = await result.json()
            if (result.ok) {
                const user = responseBody as LoginResponse
                const gaUserData = {
                    loggedInStatus: true,
                    userType: user.is_staff ? 'staff' : user.user_type,
                    verifiedStatus: user.is_verified,
                    userID: `${user.id}`,
                    loginMethod: 'email',
                } satisfies Parameters<typeof setUser>[0]
                setUser(gaUserData)
                setGADataLayer({
                    event: 'login_form_success',
                    options: {
                        user: gaUserData,
                        form: {
                            formName: 'login',
                        },
                    },
                })
                const redirect = user.redirect_url || '/'
                if (mode === 'modal') {
                    const authenticatedUser = await refreshUser?.()
                    if (!authenticatedUser) {
                        showMessage({
                            message: copy.errors.refreshSession,
                            type: 'error',
                        })
                        return
                    }
                    await onSuccess?.(authenticatedUser)
                } else {
                    // don't use router.push because it could be legacy
                    location.href = redirect
                }
            } else {
                showMessage({
                    message: getErrorMessages(responseBody),
                    type: 'error',
                })
                setGADataLayer({
                    event: 'login_form_error',
                    options: {
                        form: {
                            formName: 'login',
                            formError: 'email,password',
                        },
                    },
                })
            }
        } catch (error) {
            const isTimeout =
                error instanceof TimeoutError ||
                (error instanceof Error && error.message.startsWith('Timeout 10000 exceeded for url:'))

            showMessage({
                message: isTimeout ? copy.errors.timeout : copy.errors.requestFailed,
                type: 'error',
            })
            setGADataLayer({
                event: 'login_form_error',
                options: {
                    form: {
                        formName: 'login',
                        formError: isTimeout ? 'timeout' : 'request_failed',
                    },
                },
            })

            if (!isTimeout) {
                console.error('Login request failed', error)
            }
        } finally {
            loginInFlight.current = false
            setLoading(false)
        }
    }
    return (
        <>
            <Form
                ref={form}
                onSubmit={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    if (form.current?.checkValidity()) {
                        login()
                    }
                }}
            >
                <div>
                    <input
                        autoComplete="username"
                        placeholder={copy.emailPlaceholder}
                        name="email"
                        required
                        type="email"
                        ref={emailInput}
                        onKeyUp={forceUpdate}
                        onInput={forceUpdate}
                        className="bg-giphyWhite placeholder-inputPlaceholder"
                    />
                    <div className="error-message">{emailInput.current?.validationMessage}</div>
                </div>
                <div style={{ position: 'relative' }}>
                    <input
                        name="password"
                        placeholder={copy.passwordPlaceholder}
                        autoComplete="current-password"
                        required
                        type={viewPassword ? 'text' : 'password'}
                        minLength={6}
                        onKeyUp={forceUpdate}
                        onInput={forceUpdate}
                        className="bg-giphyWhite placeholder-inputPlaceholder"
                        ref={passwordInput}
                    />
                    <PasswordVisibility
                        ariaLabel={viewPassword ? copy.passwordVisibility.hide : copy.passwordVisibility.show}
                        viewPassword={viewPassword}
                        setViewPassword={setViewPassword}
                    />
                    <div className="error-message">{passwordInput.current?.validationMessage}</div>
                </div>
                <Button
                    size="medium"
                    gradient="purple-indigo"
                    loading={loading}
                    disabled={!form.current?.checkValidity()}
                    type="submit"
                >
                    {copy.submit}
                </Button>
            </Form>
            <ForgotPassword href="/password/reset">{copy.forgotPassword}</ForgotPassword>
            <SocialButtons>
                <Button
                    size="medium"
                    color={giphyDarkestGrey}
                    disabled={socialAuthPending}
                    href={mode === 'modal' ? undefined : facebookAuthHref}
                    onClick={mode === 'modal' ? () => openSocialAuthPopup('facebook', facebookAuthHref) : undefined}
                >
                    <SocButton>
                        {}
                        <img src={fbLogo} alt="Facebook" className="h-[1.875rem]" style={{ margin: -6 }} />
                        {copy.facebookLogin}
                    </SocButton>
                </Button>
                <Button
                    size="medium"
                    color={giphyDarkestGrey}
                    disabled={socialAuthPending}
                    href={mode === 'modal' ? undefined : appleAuthHref}
                    onClick={mode === 'modal' ? () => openSocialAuthPopup('apple', appleAuthHref) : undefined}
                >
                    <SocButton>
                        {}
                        <img src={appleLogo} alt="Apple" className="h-5" />
                        {copy.appleLogin}
                    </SocButton>
                </Button>
            </SocialButtons>
            {mode === 'modal' && socialAuthState.status !== 'idle' && (
                <div className="text-center text-xs leading-snug font-semibold text-giphyWhite">
                    {socialAuthState.status === 'pending' ? (
                        copy.socialAuth.pending
                    ) : (
                        <>
                            {copy.socialAuth.finishedPrompt}{' '}
                            <button
                                className="cursor-pointer border-0 bg-transparent p-0 font-bold text-giphyBlue hover:text-giphyWhite"
                                type="button"
                                onClick={completeSocialAuth}
                            >
                                {copy.socialAuth.checkLogin}
                            </button>
                        </>
                    )}
                </div>
            )}
            <Terms>
                {copy.terms.prefix} <a href={`/terms`}>{copy.terms.termsOfService}</a> {copy.terms.conjunction}{' '}
                <a href="https://support.giphy.com/hc/en-us/articles/360032872931">{copy.terms.privacyPolicy}</a>
            </Terms>
        </>
    )
}
export default LoginForm
